import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { History, Filter } from "lucide-react";
import { format, parseISO } from "date-fns";
import { mdyTime } from "@/lib/dateFmt";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

export default function AuditLogPanel() {
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("all");
  const [categories, setCategories] = useState([{ id: "all", label: "All" }]);

  useEffect(() => {
    let alive = true;
    api.get("/audit/categories").then(({ data }) => { if (alive) setCategories(data); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/audit?limit=200${category === "all" ? "" : `&category=${category}`}`);
        if (alive) setItems(data);
      } catch { if (alive) setItems([]); }
    })();
    return () => { alive = false; };
  }, [category]);

  const activeLabel = useMemo(
    () => categories.find(c => c.id === category)?.label || "All",
    [categories, category]
  );

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="audit-log">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-lg font-display font-semibold">
            <History size={18} className="text-blue-600" /> Audit History
          </div>
          <div className="text-sm text-slate-500">
            Every change to the schedule is logged{category !== "all" && (<> — filtered by <b>{activeLabel}</b></>)}.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-slate-500" />
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-[220px] h-9" data-testid="audit-category-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {categories.map(c => (
                <SelectItem key={c.id} value={c.id} data-testid={`audit-category-${c.id}`}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="divide-y divide-slate-100">
        {items.map((a) => (
          <div key={a.id} className="py-2.5 flex items-start gap-3" data-testid={`audit-${a.id}`}>
            <div className="w-2 h-2 rounded-full bg-blue-500 mt-2 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-slate-800">
                <span className="font-medium">{a.actor_name || "Someone"}</span>{" "}
                <span className="text-slate-700">{a.display_message || `${a.action} · ${a.target}`}</span>
              </div>
              <div className="text-xs text-slate-500 tabular-nums flex items-center gap-2">
                <span>{mdyTime(a.created_at)}</span>
                {a.category && a.category !== "other" && (
                  <span className="uppercase tracking-widest text-[10px] font-bold text-slate-400">· {a.category}</span>
                )}
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-sm text-slate-500 py-4">No activity in this category.</div>}
      </div>
    </div>
  );
}
